import type { AxiosResponse } from 'axios'

import configs from '@acre/config'

import request from '../requesters/default'

const { API1_URL, CLIENT_PORTAL_API_URL, CLIENT_PORTAL_OATHKEEPER_API_URL } = configs

// KBA
export const getKbaQuestionnaire = async (id: string, useClientApi?: boolean | null) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  const response: AxiosResponse<KbaAttempt> = await request.get<KbaAttempt>('/kba', {
    baseURL: baseURL,
    params: { client_id: id },
  })

  return response.data
}

export const submitKba = async (id: string, input: KbaInput, useClientApi?: boolean | null) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  const response = await request.post<KbaSubmissionResponse>('/kba', input, {
    baseURL: baseURL,
    params: { client_id: id },
  })

  return response.data
}

// Quotation Credit Search
export const runQuotationCreditSearch = async (clientIds: string[]) => {
  const response = await request.post(
    '/equifax_credit_search',
    {},
    {
      baseURL: `${API1_URL}/acre/checks-service`,
      params: { client_id: clientIds, quotation: 'true' },
    },
  )
  return response.status === 200
}

// Credit Report
export interface CreditReportRequest {
  client_id: string
  document_id?: string | null
}

export const buildCreditReportBody = (
  requests: CreditReportRequest[],
): { reports: { client_id: string; doc_id?: string }[] } => ({
  reports: requests.map(({ client_id, document_id }) =>
    document_id ? { client_id, doc_id: document_id } : { client_id },
  ),
})

interface CreditReportPostEntry {
  client_id?: string
  report?: CreditReport | null
}

type CreditReportPostResponse = CreditReportPostEntry[] | { reports?: CreditReportPostEntry[] | null } | null

// The POST route responds with { client_id, report } entries (wrapped in a reports
// key, mirroring the request body); fold each entry's client_id into its report so
// consumers can attribute reports by id rather than array position. Entry order is
// still preserved for same-client requests (current + previous document), where
// position is the only differentiator.
export const toCreditReportResponse = (data: CreditReportPostResponse | undefined): CreditReportResponse => {
  const entries = Array.isArray(data) ? data : data?.reports ?? []
  return {
    reports: entries.flatMap((entry) => (entry?.report ? [{ ...entry.report, client_id: entry.client_id }] : [])),
  }
}

export const getCreditReport = async (
  useClientApi?: boolean | null,
  clientId?: string | null,
  documentId?: string | null,
  requests?: CreditReportRequest[] | null,
) => {
  const useOathKeeper = sessionStorage.getItem('useOathKeeper')

  let baseURL
  if (useOathKeeper === 'true') {
    baseURL = `${CLIENT_PORTAL_OATHKEEPER_API_URL}/checks-service`
  } else if (useClientApi) {
    baseURL = `${CLIENT_PORTAL_API_URL}/checks-service`
  } else {
    baseURL = `${API1_URL}/acre/checks-service`
  }

  // Multi-report requests use the POST route: query params can't express which doc
  // belongs to which client, so the pairs go in a structured body instead. The
  // combined view is broker-only; the client-portal endpoint stays single-client,
  // so multi-report requests never use the client API or OathKeeper base URL.
  if (!useClientApi && useOathKeeper !== 'true' && requests?.length) {
    const response: AxiosResponse<CreditReportPostResponse> = await request.post<CreditReportPostResponse>(
      '/credit',
      buildCreditReportBody(requests),
      { baseURL },
    )
    return toCreditReportResponse(response.data)
  }

  const params: Record<string, string> = {}
  if (!useClientApi && clientId) params.client_id = clientId
  if (documentId) params.doc_id = documentId

  const response: AxiosResponse<CreditReportResponse> = await request.get<CreditReportResponse>('/credit', {
    baseURL,
    params,
  })

  return response.data
}

// Credit Refresh
interface CreditRefreshRestResponse {
  status?: string
  reason?: {
    code?: string
    description?: string
  }
}

const CREDIT_REFRESH_STATUSES: CreditRefreshStatus[] = ['AVAILABLE', 'UNAVAILABLE', 'REFRESHED', 'IN_PROGRESS']

// The endpoint returns lowercase statuses; the schema enum is uppercase. An
// unrecognised status maps to null so it degrades to a disabled button rather
// than a schema serialization error
const toCreditRefreshStatus = (status?: string): CreditRefreshStatus | null =>
  CREDIT_REFRESH_STATUSES.find((s) => s === status?.toUpperCase()) ?? null

export const refreshCredit = async (clientId: string, dryRun: boolean): Promise<CreditRefreshResult> => {
  const response: AxiosResponse<CreditRefreshRestResponse> = await request.post<CreditRefreshRestResponse>(
    '/credit/refresh',
    undefined,
    {
      baseURL: `${API1_URL}/acre/checks-service`,
      params: { client_id: clientId, dry_run: dryRun },
    },
  )

  const { status, reason } = response.data
  return {
    status: toCreditRefreshStatus(status),
    reason: reason ?? null,
  }
}

// Suitability Report
// Send client email notification to sign their SR
export const notifyClientFetchSr = async (input: SrNotificationInput) => {
  const baseURL = `${API1_URL}/acre/checks-service`
  const response = await request.post<SrNotificationResponse>('/notify_client_fetch_sr', input, {
    baseURL: baseURL,
  })

  return response.data
}
